<template>
  <section id="periodic-follow-ups" class="periodic-follow-up form-card mt-4" dir="rtl" aria-label="المتابعة الدورية">
    <div class="form-card__header d-flex justify-content-between flex-wrap gap-2">
      <component :is="studentId ? 'h2' : 'h1'" class="form-card__title mb-0"><i class="pi pi-list-check me-2" aria-hidden="true"></i>المتابعة الدورية</component>
      <div class="d-flex gap-2 flex-wrap">
        <router-link v-if="!studentId" class="btn btn-secondary" to="/follow-up-types">أنواع المتابعات</router-link>
        <button type="button" class="btn PrimaryButton" :disabled="saving" @click="openForm()">+ إضافة متابعة</button>
      </div>
    </div>
    <div class="form-card__body">
      <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
      <form v-if="!studentId" class="row g-3 mb-3" @submit.prevent="applyFilters">
        <div class="col-lg-3 col-md-6 form-group"><label for="follow-up-name-filter">اسم الطالب</label><input id="follow-up-name-filter" v-model="filters.student_name" type="search" class="form-control" maxlength="255"></div>
        <div class="col-lg-3 col-md-6 form-group"><label for="follow-up-number-filter">رقم الطالب / الحجز</label><input id="follow-up-number-filter" v-model="filters.student_number" type="search" class="form-control" maxlength="100"></div>
        <div class="col-lg-3 col-md-6 form-group d-flex flex-column"><label for="follow-up-type-filter">نوع المتابعة</label><Dropdown inputId="follow-up-type-filter" v-model="filters.follow_up_type_id" :options="filterTypes" optionLabel="name" optionValue="id" showClear placeholder="كل الأنواع" :pt="dropdownPanel" /></div>
        <div class="col-lg-3 col-md-6 form-group d-flex flex-column"><label for="follow-up-academic-year-filter">السنة الدراسية</label><Dropdown inputId="follow-up-academic-year-filter" v-model="filters.academic_year_id" :options="gradeStore.activeGrades" optionLabel="name" optionValue="id" showClear placeholder="كل السنوات الدراسية" :pt="dropdownPanel" /></div>
        <div class="col-lg-3 col-md-6 form-group d-flex flex-column"><label for="follow-up-status-filter">حالة واتساب</label><Dropdown inputId="follow-up-status-filter" v-model="filters.whatsapp_status" :options="statusOptions" optionLabel="label" optionValue="value" showClear placeholder="كل الحالات" :pt="dropdownPanel" /></div>
        <div class="col-lg-3 col-md-6 form-group"><label for="follow-up-date-from">من تاريخ</label><input id="follow-up-date-from" v-model="filters.date_from" type="date" class="form-control"></div>
        <div class="col-lg-3 col-md-6 form-group"><label for="follow-up-date-to">إلى تاريخ</label><input id="follow-up-date-to" v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from || undefined"></div>
        <div class="col-lg-6 d-flex align-items-end gap-2"><button type="submit" class="btn PrimaryButton" :disabled="loading">بحث</button><button type="button" class="btn btn-secondary" :disabled="loading" @click="resetFilters">مسح الفلاتر</button></div>
      </form>
      <div v-if="loading" class="text-center p-3"><i class="pi pi-spin pi-spinner"></i> جاري التحميل...</div>
      <template v-else>
        <FollowUpRatingSummary v-if="studentId && !error && history.rating_summary" :summary="history.rating_summary" />
        <div class="table-responsive">
          <table class="table table-striped align-middle">
            <thead><tr><th v-if="!studentId">الطالب</th><th v-if="!studentId">رقم الطالب / الحجز</th><th>التاريخ</th><th>نوع المتابعة</th><th>نص المتابعة</th><th>التقييم</th><th>حالة الإرسال</th><th>الإجراءات</th></tr></thead>
            <tbody>
              <tr v-for="record in history.data" :key="record.id">
                <td v-if="!studentId"><router-link :to="`/user/edit/${record.student_id}#periodic-follow-ups`">{{ record.student.name }}</router-link></td>
                <td v-if="!studentId" class="text-nowrap"><div>رقم الطالب: {{ record.student.id }}</div><small>رقم الحجز: {{ record.student.booking_number ?? '—' }}</small></td>
                <td class="text-nowrap">{{ formatDate(record.created_at) }}</td>
                <td>{{ record.type.name }}</td>
                <td><div class="follow-up-content">{{ record.content }}</div></td>
                <td><FollowUpRating :model-value="record.rating" readonly /></td>
                <td>
                  <span :class="['badge', statusClass(record.whatsapp_status)]">{{ statusLabel(record.whatsapp_status) }}</span>
                  <span v-if="record.delivery_channel" class="badge bg-light text-dark border ms-1"><i :class="record.delivery_channel === 'sms' ? 'pi pi-comment' : 'pi pi-whatsapp'"></i> {{ record.delivery_channel === 'sms' ? 'SMS' : 'WhatsApp' }}</span>
                  <small v-if="record.whatsapp_sent_at" class="d-block mt-1">{{ formatDate(record.whatsapp_sent_at) }}</small>
                  <small v-if="record.whatsapp_error" class="d-block text-danger mt-1">{{ record.whatsapp_error }}</small>
                  <small v-if="record.changed_after_sending" class="d-block text-warning mt-1">تم تعديل المتابعة بعد إرسال الرسالة الأصلية</small>
                  <details v-if="record.whatsapp_status === 'sent'" class="mt-1">
                    <summary>الرسالة المرسلة</summary><div class="follow-up-content">{{ record.whatsapp_message }}</div>
                  </details>
                </td>
                <td class="text-nowrap">
                  <button type="button" class="btn btn-primary btn-sm mx-1" :disabled="record.whatsapp_status === 'pending' || saving" aria-label="تعديل المتابعة" @click="openForm(record)"><i class="pi pi-pencil"></i></button>
                  <button type="button" class="btn btn-danger btn-sm mx-1" :disabled="record.whatsapp_status === 'pending' || saving" aria-label="حذف المتابعة" @click="remove(record)"><i class="pi pi-trash"></i></button>
                </td>
              </tr>
              <tr v-if="!history.data.length"><td :colspan="studentId ? 6 : 8" class="text-center text-muted py-4">{{ studentId ? 'لا توجد متابعات دورية لهذا الطالب' : 'لا توجد متابعات مطابقة للبحث' }}</td></tr>
            </tbody>
          </table>
        </div>
        <pagination :data="history" :limit="2" @pagination-change-page="loadHistory">
          <template #prev-nav>السابق</template><template #next-nav>التالي</template>
        </pagination>
      </template>
    </div>

    <Dialog v-model:visible="visible" class="periodic-follow-up" modal :header="editingId ? 'تعديل المتابعة الدورية' : 'إضافة متابعة دورية'"
            :style="{ width: '38rem', maxWidth: '95vw' }" :closable="!saving" :closeOnEscape="!saving" dir="rtl">
      <form @submit.prevent="save">
        <div v-if="formError" class="alert alert-danger" role="alert">{{ formError }}</div>
        <div class="form-group d-flex flex-column mb-3">
          <label for="follow-up-academic-year">السنة الدراسية *</label>
          <Dropdown inputId="follow-up-academic-year" v-model="form.academic_year_id" :options="academicYearOptions" optionLabel="name" optionValue="id" placeholder="اختر السنة الدراسية" :disabled="saving || (!!studentId && !academicYearOptions.length)" :pt="dropdownPanel" />
          <small v-if="!!studentId && !academicYearOptions.length" class="d-block text-danger mt-1">لا توجد سنة دراسية متاحة لهذا الطالب. لا يمكن إضافة متابعة له.</small>
        </div>
        <div v-if="!studentId" class="mb-3">
          <template v-if="!editingId">
            <label for="follow-up-student-search">ابحث عن الطالب بالاسم أو رقم الطالب / الحجز *</label>
            <div class="d-flex gap-2 mb-2"><input id="follow-up-student-search" v-model="studentSearch" type="search" maxlength="100" class="form-control" :disabled="saving || studentsLoading" @keydown.enter.prevent="searchStudents()"><button type="button" class="btn btn-secondary text-nowrap" :disabled="saving || studentsLoading || !studentSearch.trim() || !form.academic_year_id" @click="searchStudents()">بحث عن طالب</button></div>
            <small v-if="!form.academic_year_id" class="d-block text-muted mb-2">يرجى اختيار السنة الدراسية أولاً</small>
            <div v-if="studentSearchError" class="alert alert-danger" role="alert">{{ studentSearchError }}</div>
            <label for="follow-up-student">الطالب *</label>
            <Dropdown inputId="follow-up-student" v-model="selectedStudent" :options="students.data" :optionLabel="studentLabel" placeholder="اختر الطالب من نتائج البحث" class="w-100" :disabled="saving || studentsLoading" :pt="dropdownPanel" />
            <small v-if="studentsSearched && !studentsLoading && !students.data.length" class="d-block text-muted">لا توجد نتائج لهذا البحث</small>
            <pagination :data="students" :limit="2" @pagination-change-page="searchStudents"><template #prev-nav>السابق</template><template #next-nav>التالي</template></pagination>
          </template>
          <p v-if="selectedStudent" class="mt-2 mb-0">الطالب المحدد: <strong>{{ studentLabel(selectedStudent) }}</strong></p>
        </div>
        <div class="form-group d-flex flex-column mb-3">
          <label for="follow-up-type">نوع المتابعة *</label>
          <Dropdown inputId="follow-up-type" v-model="form.follow_up_type_id" :options="types" optionLabel="name" optionValue="id" placeholder="اختر نوع المتابعة" :disabled="saving || typesLoading" :pt="{ overlay: { class: 'periodic-follow-up-options', dir: 'rtl' } }" @change="onFollowUpTypeChange" />
          <small v-if="!typesLoading && !types.length" class="text-warning mt-1">لا توجد أنواع متابعة فعالة. أضف أو فعّل نوعًا من إدارة أنواع المتابعة.</small>
        </div>
        <div class="form-group mb-3">
          <label for="follow-up-content">نص المتابعة *</label>
          <textarea id="follow-up-content" v-model="form.content" class="form-control" rows="5" maxlength="5000" required :disabled="saving"></textarea>
        </div>
        <div class="form-group mb-3">
          <div class="mb-1">التقييم *</div>
          <FollowUpRating v-model="form.rating" :disabled="saving" />
          <small class="d-block text-muted">اختر من نجمة إلى خمس نجوم</small>
        </div>
        <div v-if="!editingId" class="form-group mb-3">
          <label class="d-block mb-2">طريقة الإرسال *</label>
          <div class="btn-group" role="group" aria-label="طريقة الإرسال">
            <button type="button" class="btn" :class="form.delivery_channel === 'whatsapp' ? 'btn-success' : 'btn-outline-success'"
              :disabled="saving" @click="form.delivery_channel = 'whatsapp'">
              <i class="pi pi-whatsapp me-1"></i>WhatsApp
            </button>
            <button type="button" class="btn" :class="form.delivery_channel === 'sms' ? 'btn-primary' : 'btn-outline-primary'"
              :disabled="saving" @click="form.delivery_channel = 'sms'">
              <i class="pi pi-comment me-1"></i>SMS
            </button>
          </div>
          <small class="d-block text-muted mt-1">سيتم حفظ المتابعة وإرسالها إلى رقم ولي الأمر المسجل للطالب عبر القناة المحددة.</small>
        </div>
        <p v-if="editingId" class="text-muted small">التعديل يحفظ المتابعة فقط ولا يعيد إرسال الرسالة. تبقى حالة الإرسال خاصة بالرسالة الأصلية.</p>
        <div class="d-flex gap-2 justify-content-end">
          <button type="button" class="btn btn-secondary" :disabled="saving" @click="visible = false">إلغاء</button>
          <button type="submit" class="btn PrimaryButton" :disabled="saving || typesLoading || (!studentId && !selectedStudent) || !form.academic_year_id || !form.follow_up_type_id || !form.rating || !form.content.trim() || (!editingId && !form.delivery_channel)">
            <i v-if="saving" class="pi pi-spin pi-spinner"></i>
            {{ saving ? 'جاري الحفظ...' : (!editingId ? 'حفظ وإرسال لولي الأمر' : 'حفظ المتابعة') }}
          </button>
        </div>
      </form>
    </Dialog>
  </section>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import pagination from 'laravel-vue-pagination';
import FollowUpRating from './FollowUpRating.vue';
import FollowUpRatingSummary from './FollowUpRatingSummary.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  components: { Dialog, Dropdown, pagination, FollowUpRating, FollowUpRatingSummary },
  props: { studentId: { type: Number, default: null } },
  data() {
    // Default "من تاريخ"/"إلى تاريخ" to today so the very first request is
    // already scoped to today's follow-ups, never an unfiltered full history.
    const today = new Date().toISOString().slice(0, 10);
    return { history: { data: [] }, loading: false, error: '', visible: false, saving: false, todayDate: today,
      filters: { student_name: '', student_number: '', follow_up_type_id: null, academic_year_id: null, date_from: today, date_to: today, whatsapp_status: null },
      appliedFilters: { date_from: today, date_to: today },
      filterTypes: [], dropdownPanel: { overlay: { class: 'periodic-follow-up-options', dir: 'rtl' } },
      statusOptions: [{ value: 'not_sent', label: 'لم يتم الإرسال' }, { value: 'pending', label: 'جاري الإرسال' }, { value: 'sent', label: 'تم الإرسال' }, { value: 'failed', label: 'فشل الإرسال' }],
      selectedStudent: null, studentSearch: '', students: { data: [] }, studentsLoading: false, studentsSearched: false, studentSearchError: '',
      // The fixed student's own summary (incl. academic_year) when this widget
      // is embedded on a student's profile (studentId prop) — populated from
      // loadHistory()'s response, which already resolves this student server-side.
      fixedStudent: null,
      types: [], typesLoading: false, editingId: null, formError: '',
      // Tracks the default description last auto-filled into form.content, so a
      // later type change can tell "still the previous default (safe to
      // replace)" apart from "the user edited it manually (must preserve)".
      lastAppliedDefault: '',
      form: { follow_up_type_id: null, academic_year_id: null, content: '', rating: null, delivery_channel: null, request_id: null } };
  },
  computed: {
    gradeStore() { return useGradeStore(); },
    // Fixed-student widget (studentId prop, e.g. embedded on a student's own
    // profile): there's nothing to search for, so the year is derived from
    // that one student (at most one option, matching users.academic_year_id).
    // Free search mode (the main "المتابعة الدورية" page): the admin picks
    // ANY active academic year FIRST — this is what the student search below
    // is then scoped to server-side — so every active year is offered.
    academicYearOptions() {
      if (this.studentId) {
        return this.fixedStudent?.academic_year ? [this.fixedStudent.academic_year] : [];
      }
      return this.gradeStore.activeGrades;
    },
  },
  watch: {
    // Free search mode only: changing the academic year invalidates any
    // student already searched/selected under a different year — never
    // search across it, never leave a stale selection lying around. Editing
    // an existing record never changes academic_year_id via this dropdown's
    // normal interaction, and the fixed-student widget has no search step,
    // so both are guarded out here.
    'form.academic_year_id'(newValue, oldValue) {
      if (this.studentId || this.editingId || newValue === oldValue) return;
      this.selectedStudent = null;
      this.students = { data: [] };
      this.studentsSearched = false;
      this.studentSearchError = '';
      this.studentSearch = '';
    },
  },
  async mounted() {
    try {
      await this.loadHistory();
      if (!this.studentId) {
        const { data } = await axios.get('/follow-ups/types'); this.filterTypes = data.data;
        // Reuses the shared grade store — a no-op request if another page already
        // populated it this session, so this never duplicates the API call.
        if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      }
      if (this.studentId && this.$route?.hash === '#periodic-follow-ups') {
        await this.$nextTick(); this.$el.scrollIntoView({ block: 'start' });
      }
    }
    catch (error) { this.error = this.errorMessage(error); }
  },
  methods: {
    studentLabel(student) { return `${student.name} — رقم الطالب: ${student.id} — رقم الحجز: ${student.booking_number ?? '—'}`; },
    async searchStudents(page = 1) {
      if (this.studentId || this.editingId || this.studentsLoading || !this.studentSearch.trim()) return;
      if (!this.form.academic_year_id) { this.studentSearchError = 'يرجى اختيار السنة الدراسية أولاً'; return; }
      this.studentsLoading = true; this.studentSearchError = ''; this.studentsSearched = true; this.selectedStudent = null;
      try {
        // academic_year_id is REQUIRED server-side (StudentFollowUpController::students())
        // — the search is scoped to it at the database level, never filtered
        // client-side after the fact.
        const { data } = await axios.get('/follow-ups/students', { params: { q: this.studentSearch.trim(), academic_year_id: this.form.academic_year_id, page } });
        this.students = data.data;
      }
      catch (error) { this.students = { data: [] }; this.studentSearchError = this.errorMessage(error); }
      finally { this.studentsLoading = false; }
    },
    async applyFilters() {
      this.appliedFilters = Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value !== '' && value !== null));
      await this.loadHistory();
    },
    async resetFilters() {
      // Restore the page's default view (today/today) rather than dropping to an
      // unfiltered full history — consistent with the initial-load default.
      this.filters = { student_name: '', student_number: '', follow_up_type_id: null, academic_year_id: null, date_from: this.todayDate, date_to: this.todayDate, whatsapp_status: null };
      await this.applyFilters();
    },
    errorMessage(error) {
      const errors = error.response?.data?.errors;
      return errors ? Object.values(errors).flat().join(' — ') : (error.response?.data?.message || 'تعذر إتمام العملية. حاول مرة أخرى');
    },
    async loadHistory(page = 1) {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.get(this.studentId ? `/students/${this.studentId}/follow-ups` : '/follow-ups', { params: { ...(!this.studentId ? this.appliedFilters : {}), page } });
        this.history = data;
        if (this.studentId && data.student) this.fixedStudent = data.student;
      }
      catch (error) { this.error = this.errorMessage(error); }
      finally { this.loading = false; }
    },
    // Fixed-student widget only (studentId prop): auto-selects that one
    // student's academic year, or clears the field when none is available.
    // In free search mode the admin must pick a year explicitly — there is
    // no "student" yet to derive one from.
    syncFixedStudentAcademicYear() {
      if (!this.studentId) return;
      const options = this.academicYearOptions;
      this.form.academic_year_id = options.length === 1 ? options[0].id : null;
    },
    async openForm(record = null) {
      if (this.saving) return;
      this.editingId = record?.id ?? null;
      this.selectedStudent = record?.student ?? null;
      this.studentSearch = ''; this.students = { data: [] }; this.studentsSearched = false; this.studentSearchError = '';
      this.form = { follow_up_type_id: record?.type.is_active ? record.follow_up_type_id : null,
        // Editing loads the follow-up's OWN stored academic year (a snapshot
        // taken at creation time) — never re-derived from the student's
        // current one, which may have changed since.
        academic_year_id: record?.academic_year_id ?? null,
        content: record?.content ?? '', rating: record?.rating ?? null, delivery_channel: null,
        request_id: crypto.randomUUID() };
      // New follow-up on the fixed-student widget: auto-select immediately —
      // nothing to search for, so there's no other moment this would run.
      if (!record) this.syncFixedStudentAcademicYear();
      // No default has been auto-filled yet in this dialog session — an existing
      // record's saved content must never be treated as "still the default".
      this.lastAppliedDefault = '';
      this.formError = '';
      this.types = [];
      this.visible = true;
      this.typesLoading = true;
      try { const { data } = await axios.get('/follow-up-types/options'); this.types = data.data; }
      catch (error) { this.formError = this.errorMessage(error); }
      finally { this.typesLoading = false; }
    },
    // The selected type's description is only a starting value for "نص
    // المتابعة" — never overwrite text the user already typed themselves.
    // Safe to replace only when the textarea is empty or still holds exactly
    // the previously-selected type's default (i.e. untouched by the user).
    onFollowUpTypeChange(event) {
      const selectedId = event?.value ?? this.form.follow_up_type_id;
      const selectedType = this.types.find(type => type.id === selectedId);
      const newDefault = selectedType?.description || '';
      if (!this.form.content.trim() || this.form.content === this.lastAppliedDefault) {
        this.form.content = newDefault;
      }
      this.lastAppliedDefault = newDefault;
    },
    async save() {
      if (this.saving) return;
      const targetStudentId = this.studentId || this.selectedStudent?.id;
      if (!targetStudentId) { this.formError = 'اختر الطالب من نتائج البحث أولاً'; return; }
      if (!this.form.academic_year_id) { this.formError = 'اختر السنة الدراسية'; return; }
      if (!this.form.follow_up_type_id || !this.form.content.trim() || !Number.isInteger(this.form.rating) || this.form.rating < 1 || this.form.rating > 5) {
        this.formError = 'اختر نوع المتابعة وأدخل النص والتقييم من 1 إلى 5';
        return;
      }
      if (!this.editingId && !this.form.delivery_channel) { this.formError = 'اختر طريقة الإرسال: WhatsApp أو SMS'; return; }
      this.saving = true;
      this.formError = '';
      try {
        const url = `/students/${targetStudentId}/follow-ups`;
        const payload = { follow_up_type_id: this.form.follow_up_type_id, academic_year_id: this.form.academic_year_id, content: this.form.content, rating: this.form.rating };
        const { data } = this.editingId
          ? await axios.put(`${url}/${this.editingId}`, payload)
          : await axios.post(url, { ...payload, request_id: this.form.request_id,
              delivery_channel: this.form.delivery_channel });
        this.visible = false;
        const status = data.data.whatsapp_status;
        Swal.fire({ confirmButtonText: 'حسنًا', icon: status === 'failed' ? 'warning' : status === 'pending' ? 'info' : 'success',
          title: status === 'failed' ? 'تم الحفظ مع تعذر الإرسال' : 'تم الحفظ', text: data.message });
        if (!this.studentId && !this.editingId) await this.resetFilters();
        else await this.loadHistory();
      } catch (error) { this.formError = this.errorMessage(error); }
      finally { this.saving = false; }
    },
    async remove(record) {
      const result = await Swal.fire({ icon: 'warning', title: 'حذف المتابعة؟', showCancelButton: true, confirmButtonText: 'حذف', cancelButtonText: 'إلغاء' });
      if (!result.isConfirmed || this.saving) return;
      this.saving = true;
      try { await axios.delete(`/students/${this.studentId || record.student_id}/follow-ups/${record.id}`); await this.loadHistory(); }
      catch (error) { Swal.fire({ confirmButtonText: 'حسنًا', icon: 'error', title: 'تعذر الحذف', text: this.errorMessage(error) }); }
      finally { this.saving = false; }
    },
    formatDate(value) { return value ? new Date(value).toLocaleString('ar-EG') : '—'; },
    statusLabel(status) { return { not_sent: 'لم يتم الإرسال', pending: 'جاري الإرسال', sent: 'تم الإرسال', failed: 'فشل الإرسال' }[status] || status; },
    statusClass(status) { return { not_sent: 'bg-secondary', pending: 'bg-warning text-dark', sent: 'bg-success', failed: 'bg-danger' }[status]; },
  },
};
</script>

<style scoped>
.follow-up-content { white-space: pre-wrap; overflow-wrap: anywhere; min-width: 10rem; max-width: 35rem; }
th, td { text-align: start; }
</style>
<style src="./followUps.css"></style>
